import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  Slate,
  Editable,
  withReact,
  ReactEditor,
  RenderElementProps,
} from 'slate-react';
import { createEditor, Node } from 'slate';
import { withHistory } from 'slate-history';
// Helpers
import {
  serialize,
  deserialize,
  withInlines,
  createParagraphNode,
} from 'components/common/RichText/helpers/basic';
import { isJsonString } from 'helpers/textEditor';
// Components
import Leaf, {
  LeafProps,
} from 'components/common/RichText/components/Leaf/Leaf';
import Toolbar, {
  ToolbarItem,
} from 'components/common/RichText/components/Toolbar/Toolbar';
import Element from 'components/common/RichText/components/Element/Element';

export type RichTextProps = {
  onChange: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  initialValue?: string;
  reset?: boolean;
  toolbarItems?: ToolbarItem[];
};

// Note: this component can't be covered by unit tests for now because
// the issue caused by the Editable component from the 'slate-react' package
const RichText = ({
  readOnly,
  placeholder = '',
  onChange,
  initialValue,
  reset,
  toolbarItems,
}: RichTextProps): React.ReactElement => {
  const [value, setValue] = useState<Node[]>([createParagraphNode('')]);
  const [editor] = useState<ReactEditor>(() =>
    withInlines(withHistory(withReact(createEditor() as ReactEditor)))
  );

  const renderLeaf = useCallback<(props: LeafProps) => JSX.Element>(Leaf, []);
  const renderElement = useCallback<(props: RenderElementProps) => JSX.Element>(
    Element,
    []
  );

  const mountRef = useRef<boolean>(false);

  useEffect(() => {
    if ((initialValue && !mountRef.current) || (reset && initialValue)) {
      const isJson = isJsonString(initialValue);

      let initialEditorValue = [createParagraphNode(initialValue)];

      if (isJson) {
        const parsedJSON = JSON.parse(initialValue);
        const document = new DOMParser().parseFromString(
          parsedJSON,
          'text/html'
        );

        initialEditorValue = deserialize(document.body);
      }

      setValue(initialEditorValue);
      mountRef.current = true;
    }
  }, [initialValue, reset]);

  const handleChange = (value: Node[]) => {
    const serializedValue = serialize(editor);
    const content = JSON.stringify(serializedValue);
    const temp = document.createElement('div');

    temp.innerHTML = serializedValue;
    const sanitized = temp.textContent || temp.innerText;
    const contentValue = sanitized.trim().length ? content : '';

    setValue(value);
    onChange(contentValue);
  };

  return (
    <Slate editor={editor} value={value} onChange={handleChange}>
      {!readOnly && <Toolbar items={toolbarItems} />}

      <Editable
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        placeholder={placeholder}
        spellCheck
        readOnly={readOnly}
      />
    </Slate>
  );
};

export default RichText;
